import { ArrowLeft, ChevronLeft } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { StatusValue, type StatusEntry } from '@/shared/patterns';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

/** Status do registro pelo valor técnico (U19): o mapa dá rótulo, tom e ícone; fora do mapa, o rótulo da API. */
export interface DetailStatus {
  readonly value: string;
  readonly entry: StatusEntry | undefined;
  readonly fallbackLabel?: string | undefined;
}

export interface DetailHeaderProps {
  /** "Operação #48213", "FIDC Aurora Multissetorial". */
  readonly title: ReactNode;
  /** Voltar para a tela-mãe (a lista). */
  readonly back?: { readonly label: string; readonly onClick: () => void } | undefined;
  readonly status?: DetailStatus | undefined;
  /** Linha de contexto, na ordem: produto · tomador, "Criado em … por …". */
  readonly meta?: readonly ReactNode[] | undefined;
  /** A barra de ações (`ActionBar`). No iPhone, ela se prende embaixo da tela. */
  readonly actions?: ReactNode;
  /** Ao lado das ações (web) ou abaixo do status (iPhone): "Alterações não salvas", "Somente leitura". */
  readonly aside?: ReactNode;
  /** Abaixo da linha do título: KPIs, stepper, aviso de restrição. */
  readonly children?: ReactNode;
  readonly className?: string;
}

function Meta({ status, meta }: Pick<DetailHeaderProps, 'status' | 'meta'>) {
  const parts = meta ?? [];
  if (!status && parts.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
      {status ? <StatusValue entry={status.entry} value={status.value} fallbackLabel={status.fallbackLabel} /> : null}
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <span aria-hidden className="text-faint">
              ·
            </span>
          ) : null}
          <span className="min-w-0">{part}</span>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Cabeçalho do arquétipo Detalhe (P08, U39 §1): voltar, título, status com tom e ícone, contexto e a
 * barra de ações; embaixo, KPIs e stepper. No iPhone fica compacto (protótipo `m_detail_top`) e a
 * barra de ações vai para o rodapé preso. A tela é uma coluna flex: a barra usa `order-last`.
 */
export function DetailHeader({ title, back, status, meta, actions, aside, children, className }: DetailHeaderProps) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <>
        <header data-slot="detail-header" className={cn('flex flex-col gap-2', className)}>
          {/* No iPhone o voltar é o da topbar (trilha do manifesto, U42): aqui só na web. */}
          {back ? (
            <Button variant="link" className="-ml-1 hidden min-h-11 w-fit gap-1 text-base md:inline-flex" onClick={back.onClick}>
              <ChevronLeft aria-hidden className="size-5" />
              {back.label}
            </Button>
          ) : null}
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-foreground">{title}</h1>
          {status ? (
            <div className="flex">
              <StatusValue entry={status.entry} value={status.value} fallbackLabel={status.fallbackLabel} />
            </div>
          ) : null}
          {meta && meta.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              {meta.map((part, index) => (
                <Fragment key={index}>
                  {index > 0 ? ' · ' : null}
                  {part}
                </Fragment>
              ))}
            </p>
          ) : null}
          {aside}
          {children ? <div className="mt-1 flex flex-col gap-3">{children}</div> : null}
        </header>
        {/* Largura toda, como o `stickybar` do protótipo: o `-mx-4` desfaz o respiro do `<main>` da casca. */}
        {actions ? <div className="sticky bottom-0 z-20 order-last -mx-4">{actions}</div> : null}
      </>
    );
  }
  return (
    <header data-slot="detail-header" className={cn('flex flex-col gap-3.5', className)}>
      {back ? (
        <Button variant="link" size="sm" className="-ml-0.5 w-fit gap-1.5" onClick={back.onClick}>
          <ArrowLeft aria-hidden className="size-3.5" />
          {back.label}
        </Button>
      ) : null}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1 className="text-3xl leading-tight font-semibold tracking-tight text-foreground">{title}</h1>
          <Meta status={status} meta={meta} />
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {aside}
          {actions}
        </div>
      </div>
      {children}
    </header>
  );
}
